import { queryOptions } from '@tanstack/react-query';

import { auditLog } from '@/lib/queries/admin';
import { supabase } from '@/lib/supabase';

// SCHEMA SEAM — PLAN §4 (Streaming Browse Page).
// The `categories` and `nav_menu_items` tables landed in migration
// 20260614120000_streaming_browse_catalog (applied to prod 2026-06-15). This
// module is the single typed seam through which the Increment 1 surfaces
// (admin.categories, admin.menu, the creator CategoryPicker) talk to that
// schema. The queries/mutations below are now live (Increment 3 / "3b").

// --- types (mirror PLAN §4; replaced by Tables<'categories'> post-migration) ---

export interface Category {
  id: string;
  name: string;
  slug: string;
  sort_order: number;
  is_visible: boolean;
  created_at: string;
  updated_at: string;
}

export interface NavMenuItem {
  id: string;
  label: string;
  href: string;
  sort_order: number;
  is_visible: boolean;
  created_at: string;
  updated_at: string;
}

export interface CategoryInput {
  id?: string;
  name: string;
  slug: string;
  is_visible: boolean;
  sort_order: number;
}

export interface NavMenuItemInput {
  id?: string;
  label: string;
  href: string;
  is_visible: boolean;
  sort_order: number;
}

/** A reorder swaps the sort_order of two adjacent rows. */
export type OrderSwap = {
  a: { id: string; sort_order: number };
  b: { id: string; sort_order: number };
};

// --- pure helpers (UI logic, schema-independent, unit-tested) ---------------

/** Next sort_order for an appended row: one past the current max, else 0. */
export function nextSortOrder(rows: ReadonlyArray<{ sort_order: number }>): number {
  return rows.reduce((max, r) => Math.max(max, r.sort_order + 1), 0);
}

/**
 * Given rows sorted by sort_order, return the sort_order swap that moves `id`
 * one step in `dir` (-1 up, +1 down), or null if it is already at the edge.
 */
export function reorderSwap<T extends { id: string; sort_order: number }>(
  rows: ReadonlyArray<T>,
  id: string,
  dir: -1 | 1,
): OrderSwap | null {
  const ordered = [...rows].sort((x, y) => x.sort_order - y.sort_order);
  const idx = ordered.findIndex((r) => r.id === id);
  if (idx === -1) return null;
  const swap = ordered[idx + dir];
  const row = ordered[idx];
  if (!swap || !row) return null;
  return {
    a: { id: row.id, sort_order: swap.sort_order },
    b: { id: swap.id, sort_order: row.sort_order },
  };
}

/** A category may only be deleted when no videos are assigned to it. */
export function canDeleteCategory(assignedVideoCount: number): boolean {
  return assignedVideoCount === 0;
}

// --- queries (live) ---------------------------------------------------------

export const catalogQueries = {
  categories: () =>
    queryOptions({
      queryKey: ['catalog', 'categories'] as const,
      queryFn: async (): Promise<Category[]> => {
        const { data, error } = await supabase
          .from('categories')
          .select('*')
          .order('sort_order', { ascending: true });
        if (error) throw error;
        return (data ?? []) as Category[];
      },
    }),

  /** Visible categories only — feeds the creator CategoryPicker and browse rows. */
  visibleCategories: () =>
    queryOptions({
      queryKey: ['catalog', 'categories', 'visible'] as const,
      queryFn: async (): Promise<Category[]> => {
        const { data, error } = await supabase
          .from('categories')
          .select('*')
          .eq('is_visible', true)
          .order('sort_order', { ascending: true });
        if (error) throw error;
        return (data ?? []) as Category[];
      },
    }),

  /** Map of category_id → count of assigned videos, for delete-blocking. */
  categoryAssignedCounts: () =>
    queryOptions({
      queryKey: ['catalog', 'category-counts'] as const,
      queryFn: async (): Promise<Record<string, number>> => {
        const { data, error } = await supabase
          .from('content')
          .select('category_id')
          .eq('type', 'video')
          .not('category_id', 'is', null);
        if (error) throw error;
        const counts: Record<string, number> = {};
        for (const row of data ?? []) {
          const id = (row as { category_id: string | null }).category_id;
          if (id) counts[id] = (counts[id] ?? 0) + 1;
        }
        return counts;
      },
    }),

  menuItems: () =>
    queryOptions({
      queryKey: ['catalog', 'menu'] as const,
      queryFn: async (): Promise<NavMenuItem[]> => {
        const { data, error } = await supabase
          .from('nav_menu_items')
          .select('*')
          .order('sort_order', { ascending: true });
        if (error) throw error;
        return (data ?? []) as NavMenuItem[];
      },
    }),
};

// --- mutations (live) -------------------------------------------------------

export const catalogMutations = {
  saveCategory: async (input: CategoryInput): Promise<void> => {
    const row = {
      name: input.name,
      slug: input.slug,
      is_visible: input.is_visible,
      sort_order: input.sort_order,
    };
    if (input.id) {
      const { error } = await supabase.from('categories').update(row).eq('id', input.id);
      if (error) throw error;
      await auditLog('category.update', 'categories', input.id, { name: input.name });
    } else {
      const { data, error } = await supabase.from('categories').insert(row).select('id').single();
      if (error) throw error;
      await auditLog('category.create', 'categories', data?.id ?? null, { name: input.name });
    }
  },

  deleteCategory: async (id: string): Promise<void> => {
    // Caller has already confirmed canDeleteCategory() against categoryAssignedCounts.
    const { error } = await supabase.from('categories').delete().eq('id', id);
    if (error) throw error;
    await auditLog('category.delete', 'categories', id);
  },

  reorderCategories: async (swap: OrderSwap): Promise<void> => {
    const a = await supabase
      .from('categories')
      .update({ sort_order: swap.a.sort_order })
      .eq('id', swap.a.id);
    if (a.error) throw a.error;
    const b = await supabase
      .from('categories')
      .update({ sort_order: swap.b.sort_order })
      .eq('id', swap.b.id);
    if (b.error) throw b.error;
  },

  saveMenuItem: async (input: NavMenuItemInput): Promise<void> => {
    const row = {
      label: input.label,
      href: input.href,
      is_visible: input.is_visible,
      sort_order: input.sort_order,
    };
    if (input.id) {
      const { error } = await supabase.from('nav_menu_items').update(row).eq('id', input.id);
      if (error) throw error;
      await auditLog('menu_item.update', 'nav_menu_items', input.id, { label: input.label });
    } else {
      const { data, error } = await supabase
        .from('nav_menu_items')
        .insert(row)
        .select('id')
        .single();
      if (error) throw error;
      await auditLog('menu_item.create', 'nav_menu_items', data?.id ?? null, {
        label: input.label,
      });
    }
  },

  deleteMenuItem: async (id: string): Promise<void> => {
    const { error } = await supabase.from('nav_menu_items').delete().eq('id', id);
    if (error) throw error;
    await auditLog('menu_item.delete', 'nav_menu_items', id);
  },

  reorderMenuItems: async (swap: OrderSwap): Promise<void> => {
    const a = await supabase
      .from('nav_menu_items')
      .update({ sort_order: swap.a.sort_order })
      .eq('id', swap.a.id);
    if (a.error) throw a.error;
    const b = await supabase
      .from('nav_menu_items')
      .update({ sort_order: swap.b.sort_order })
      .eq('id', swap.b.id);
    if (b.error) throw b.error;
  },

  /**
   * Associate a freshly-created video with a category. A null categoryId is a
   * no-op (the picker yields null when the creator leaves it unset); the live
   * upload flow must never fail because of this seam.
   */
  attachCategoryToContent: async (contentId: string, categoryId: string | null): Promise<void> => {
    if (!categoryId) return;
    const { error } = await supabase
      .from('content')
      .update({ category_id: categoryId })
      .eq('id', contentId);
    if (error) throw error;
  },
};
